import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Check, X, ShieldCheck } from '@phosphor-icons/react';
import { API } from '../lib/api';
import { useSEO } from '../lib/seo';
import { COUNTRIES } from '../data/site';

const AdminShowcase = () => {
  useSEO({ title: 'Showcase Review — Vox Meridian', description: '', path: '/admin/showcase', robots: 'noindex, nofollow' });
  const [params] = useSearchParams();
  const key = params.get('key') || '';
  const [pending, setPending] = useState(null);
  const [error, setError] = useState(false);
  const [busy, setBusy] = useState('');

  useEffect(() => {
    const m = document.createElement('meta');
    m.setAttribute('name', 'robots');
    m.setAttribute('content', 'noindex, nofollow');
    document.head.appendChild(m);
  }, []);

  const load = () =>
    axios
      .get(`${API}/ads/pending`, { params: { key } })
      .then((r) => { setPending(r.data.pending || []); setError(false); })
      .catch(() => setError(true));

  useEffect(() => { if (key) load(); }, [key]);

  const act = async (id, action) => {
    setBusy(id);
    try {
      await axios.post(`${API}/ads/${id}/${action}`, null, { params: { key } });
      load();
    } catch {
      setError(true);
    } finally {
      setBusy('');
    }
  };

  return (
    <div className="mx-auto min-h-[70vh] max-w-3xl px-6 pb-24 pt-32" data-testid="admin-showcase-page">
      <div className="flex items-center gap-3">
        <ShieldCheck size={26} className="text-volt" />
        <h1 className="font-display text-3xl font-black tracking-tight text-parchment">Showcase review</h1>
      </div>
      <p className="mt-3 text-sm text-steel">Pending listings appear here. Approve puts them live with the next slot number; reject deletes them.</p>

      {!key || error ? (
        <div className="mt-10 border border-line bg-surface p-8 text-sm text-steel" data-testid="admin-showcase-denied">
          Invalid or missing review key. Use the link from the notification email.
        </div>
      ) : pending === null ? (
        <p className="mt-10 text-sm text-steel">Loading…</p>
      ) : pending.length === 0 ? (
        <div className="mt-10 border border-line bg-surface p-8 text-sm text-steel" data-testid="admin-showcase-empty">
          Nothing pending. The board is fully reviewed.
        </div>
      ) : (
        <div className="mt-10 space-y-4">
          {pending.map((ad) => (
            <div key={ad.id} data-testid={`admin-ad-${ad.id}`} className="border border-line bg-surface p-6">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="font-display text-lg font-bold text-parchment">{ad.company}</p>
                  <p className="mt-1 font-mono text-xs uppercase tracking-[0.15em] text-volt/80">
                    {ad.trade} · {COUNTRIES.find((c) => c.code === ad.country)?.name || ad.country}
                  </p>
                  <p className="mt-3 text-sm text-steel">{ad.tagline}</p>
                  <p className="mt-2 text-xs text-steel/60">{ad.email}{ad.website ? ` · ${ad.website}` : ''}</p>
                </div>
              </div>
              <div className="mt-5 flex gap-3">
                <button
                  data-testid={`admin-approve-${ad.id}`}
                  disabled={busy === ad.id}
                  onClick={() => act(ad.id, 'approve')}
                  className="flex flex-1 items-center justify-center gap-2 bg-volt px-4 py-3 font-mono text-xs font-bold uppercase tracking-[0.15em] text-ink transition-transform duration-200 hover:-translate-y-0.5 disabled:opacity-50"
                >
                  <Check size={14} weight="bold" /> Approve
                </button>
                <button
                  data-testid={`admin-reject-${ad.id}`}
                  disabled={busy === ad.id}
                  onClick={() => act(ad.id, 'reject')}
                  className="flex flex-1 items-center justify-center gap-2 border border-line px-4 py-3 font-mono text-xs font-bold uppercase tracking-[0.15em] text-steel transition-colors duration-200 hover:border-red-400 hover:text-red-400 disabled:opacity-50"
                >
                  <X size={14} weight="bold" /> Reject
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminShowcase;
